import './MessageBody.scss';
import { prefixClass } from 'lib/utils';
import { IconEmailNew, IconTaskNew, IconToDo, IconCommentsNew } from 'components/Icons';
import classNames from 'classnames';

import { Tags, IconButton } from '../../../../Common/';
import { TagProps } from '../../../../Common/Tags/Tags';
import MessageContent from '../MessageContent';
import { FormatMessageContentOptions } from '../MessageContent/formatMessageContent';
import {
  messagePreviewDetectors,
  type EmailMetadata,
  type MessageDetailsContent,
} from '../MessageContent/analyzeMessageContent';
import type { CheckListItemProps } from '../../../../Common/CheckList/CheckList';
import type { Attachment } from '../../../../../Queue/Chat/Chat';
import { formatTime } from '../../../../../Queue/RequestItem/utils/formatDateQ';
import type { AskRequest } from '@hone-automation/common';
import MessageComposer from '../../../../../Queue/CMComposer/CMComposer';
import { useQueueContext } from '../../../../../Queue/QueueContext';

type ReplyCrontrols = {
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
};

interface MessageBodyProps extends FormatMessageContentOptions {
  internal?: boolean;
  userType?: string;
  userName: string;
  dateTime?: string;
  replyControls?: ReplyCrontrols[];
  message: string;
  attachments?: Attachment[];
  checklist?: CheckListItemProps[];
  details?: MessageDetailsContent;
  isFirstMessage?: boolean;
  emailMetadata?: EmailMetadata;
  request?: AskRequest;
  // Edit operations
  isEditing?: boolean;
  onSaveEdit?: (content: string) => void;
  onCancelEdit?: () => void;
}

interface MessageUserInfoProps {
  userName: string;
  dateTime?: string;
  internal?: boolean;
  userType?: string;
  isFirstMessage?: boolean;
  emailMetadata?: EmailMetadata;
}
const MessageUserInfo = ({
  userName,
  dateTime,
  internal,
  userType,
  isFirstMessage,
  emailMetadata,
}: MessageUserInfoProps) => {
  const prefix = prefixClass('q2-message-user');
  const tags: TagProps[] = [];
  if (userType) {
    tags.push({ tag: userType, toolTip: userType, tagType: 'user-type', id: '1' });
  }
  if (internal) {
    tags.push({ tag: 'Int', toolTip: 'Internal Team', tagType: 'pill', id: '2' });
  }

  // Show email icon for valid received emails (default if status not set), but not for first message
  const hasValidEmailMetadata = messagePreviewDetectors.isValidEmailMetadata(emailMetadata);
  const showEmailIcon = !isFirstMessage && hasValidEmailMetadata && emailMetadata?.status !== 'Sent';

  return (
    <div className={prefix()}>
      <div className={prefix('user')}>{userName}</div>
      {tags.length > 0 && (
        <div className={prefix('tags')}>
          <Tags tags={tags} />
        </div>
      )}
      {showEmailIcon && (
        <div className={prefix('icon')}>
          <IconEmailNew />
        </div>
      )}
      <div className={prefix('date-time')}>{formatTime(dateTime)}</div>
    </div>
  );
};

interface MessageHeaderProps {
  replyControls?: ReplyCrontrols[];
  userName: string;
  dateTime?: string;
  internal?: boolean;
  userType?: string;
  isFirstMessage?: boolean;
  emailMetadata?: EmailMetadata;
}
const MessageHeader = ({
  replyControls,
  userName,
  dateTime,
  internal,
  userType,
  isFirstMessage,
  emailMetadata,
}: MessageHeaderProps) => {
  const prefix = prefixClass('q2-message-header');
  return (
    <div className={prefix()}>
      <MessageUserInfo
        userName={userName}
        dateTime={dateTime}
        internal={internal}
        userType={userType}
        isFirstMessage={isFirstMessage}
        emailMetadata={emailMetadata}
      />
      {!isFirstMessage && (
        <div className={prefix('reply-controls')}>
          {replyControls?.map((control, index) => (
            <IconButton
              key={index}
              icon={control.icon}
              size="small"
              tooltip={control.tooltip}
              rounded
              onClick={control.onClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MessageBody = ({
  replyControls,
  userName,
  dateTime,
  internal,
  userType,
  message,
  users,
  fallbackUser,
  skipMentionFormatting,
  attachments,
  checklist,
  details,
  isFirstMessage,
  emailMetadata,
  request,
  isEditing,
  onSaveEdit,
  onCancelEdit,
}: MessageBodyProps) => {
  const prefix = prefixClass('q2-message-body');
  const { state } = useQueueContext();

  // If in edit mode, show the MessageComposer
  if (isEditing) {
    return (
      <div className={prefix()}>
        <MessageHeader
          replyControls={replyControls}
          userName={userName}
          dateTime={dateTime}
          internal={internal}
          userType={userType}
          isFirstMessage={isFirstMessage}
          emailMetadata={emailMetadata}
        />
        <div className={`${prefix()}__edit-container`}>
          <MessageComposer
            request={state.selectedItem!}
            initialContent={message}
            isEdit={true}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
          />
        </div>
      </div>
    );
  }

  // Normal view mode
  return (
    <div className={prefix()}>
      <MessageHeader
        replyControls={replyControls}
        userName={userName}
        dateTime={dateTime}
        internal={internal}
        userType={userType}
        isFirstMessage={isFirstMessage}
        emailMetadata={emailMetadata}
      />
      <MessageContent
        message={message}
        users={users}
        fallbackUser={fallbackUser}
        skipMentionFormatting={skipMentionFormatting}
        attachments={attachments}
        checklist={checklist}
        details={details}
        emailMetadata={emailMetadata}
        request={request}
      />
    </div>
  );
};

export default MessageBody;
